import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { AdminTreatmentRepository } from './domain'

import {
  AdminTreatmentCreateRequest,
  AdminTreatmentListCriteria,
  AdminTreatmentListItem,
  AdminTreatmentModifyRequest,
  AdminTreatmentPropertiesItem,
  AdminTreatmentPropertyModifyRequest,
  AdminTreatmentPropertyRequest,
  AdminTreatmentTag,
  AdminTreatmentView,
  OperationType,
} from '../../contracts/treatment'
import { AxiosErrorHandler, SupernovaPagedBody, SupernovaResponse } from '../../foundation'

export class AdminApiTreatment implements AdminTreatmentRepository {
  private errorHandler: AxiosErrorHandler

  constructor(private axios: AxiosInstance) {
    this.errorHandler = new AxiosErrorHandler()
  }

  getTreatmentList = async (
    criteria: AdminTreatmentListCriteria,
  ): Promise<SupernovaPagedBody<AdminTreatmentListItem[]>> => {
    const config: AxiosRequestConfig<AdminTreatmentListCriteria> = {
      url: '/treatment/admin/query/treatment-list/process',
      method: 'post',
      data: criteria,
    }
    try {
      const { data } = await this.axios.request<SupernovaPagedBody<AdminTreatmentListItem[]>>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getById = async (id: number): Promise<AdminTreatmentView> => {
    const config: AxiosRequestConfig<{ id: number }> = {
      url: '/treatment/admin/query/treatment/process',
      method: 'post',
      data: {
        id,
      },
    }
    try {
      const { data } = await this.axios.request<AdminTreatmentView>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getTreatmentTagList = async (): Promise<{ id: number; name: string; operationType: OperationType }[]> => {
    const config: AxiosRequestConfig<void> = {
      url: '/admin/v1/treatments/summaries',
      method: 'get',
      params: {
        includeInactive: 'false',
      },
    }
    const { data } =
      await this.axios.request<SupernovaResponse<{ id: number; name: string; operationType: OperationType }[]>>(config)
    return data.body
  }

  getTreatmentTags = async (tagName: string): Promise<AdminTreatmentTag[]> => {
    const config: AxiosRequestConfig = {
      url: '/admin/v2/tags',
      method: 'get',
      params: {
        tagMetaNameList: tagName,
      },
    }
    const { data } = await this.axios.request<SupernovaResponse<AdminTreatmentTag[]>>(config)
    return data.body
  }

  getTreatmentProperties = async (ids: number[]): Promise<AdminTreatmentPropertiesItem[]> => {
    const config: AxiosRequestConfig = {
      url: '/treatment/admin/query/treatment-properties/process',
      method: 'post',
      data: {
        ids,
      },
    }
    const { data } = await this.axios.request<AdminTreatmentPropertiesItem[]>(config)
    return data
  }

  enableTreatment = async (id: number): Promise<void> => {
    const config: AxiosRequestConfig<{ id: number }> = {
      url: '/treatment/admin/command/enable-treatment/execute',
      method: 'post',
      data: {
        id,
      },
    }
    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  disableTreatment = async (id: number): Promise<void> => {
    const config: AxiosRequestConfig<{ id: number }> = {
      url: '/treatment/admin/command/disable-treatment/execute',
      method: 'post',
      data: {
        id,
      },
    }
    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  createPricePropertyRelation = async (command: AdminTreatmentPropertyRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminTreatmentPropertyRequest> = {
      url: '/treatment/admin/command/create-price-property-relation/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  modifyPricePropertyRelation = async (command: AdminTreatmentPropertyModifyRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminTreatmentPropertyRequest> = {
      url: '/treatment/admin/command/modify-price-property-relation/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  deletePricePropertyRelation = async (command: AdminTreatmentPropertyRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminTreatmentPropertyRequest> = {
      url: '/treatment/admin/command/delete-price-property-relation/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  createUnitPropertyRelation = async (command: AdminTreatmentPropertyRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminTreatmentPropertyRequest> = {
      url: '/treatment/admin/command/create-unit-property-relation/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  deleteUnitPropertyRelation = async (command: AdminTreatmentPropertyRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminTreatmentPropertyRequest> = {
      url: '/treatment/admin/command/delete-unit-property-relation/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  createTreatment = async (treatment: AdminTreatmentCreateRequest): Promise<{ id: number }> => {
    const config: AxiosRequestConfig<AdminTreatmentCreateRequest> = {
      url: '/treatment/admin/command/create-treatment/execute',
      method: 'post',
      data: treatment,
    }
    try {
      const { data } = await this.axios.request<{ id: number }>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  modifyTreatment = async (treatment: AdminTreatmentModifyRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminTreatmentModifyRequest> = {
      url: '/treatment/admin/command/modify-treatment/execute',
      method: 'post',
      data: treatment,
    }
    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }
}
